import { create } from 'zustand';
import { chatWithAnalysis, getChatHistory } from '../services/api';
import { formatMessageTime } from '../utils/markdown';
import { trackEvent, trackError } from '../utils/tracking';
import type { ChatMessage } from '../types';

let _chatHistoryInflight: Promise<void> | null = null;

interface ChatState {
  chatOpen: boolean;
  chatMessages: ChatMessage[];
  chatInput: string;
  chatLoading: boolean;
  chatHistoryLoading: boolean;
  copiedMessageIndex: number | null;
  exportingChat: boolean;
}

interface ChatActions {
  setChatOpen: (open: boolean) => void;
  toggleChat: () => void;
  setChatInput: (input: string) => void;
  resetChat: () => void;
  loadChatHistory: (analysisId: string) => Promise<void>;
  sendMessage: (analysisId: string, suggestedQuestion?: string | null) => Promise<void>;
  copyMessage: (message: ChatMessage, index: number, analysisId: string | null) => void;
  exportChat: (analysisId: string) => Promise<void>;
}

export const useChatStore = create<ChatState & ChatActions>((set, get) => ({
  // State
  chatOpen: false,
  chatMessages: [],
  chatInput: '',
  chatLoading: false,
  chatHistoryLoading: false,
  copiedMessageIndex: null,
  exportingChat: false,

  // Actions
  setChatOpen: (open) => set({ chatOpen: open }),
  toggleChat: () => set((s) => ({ chatOpen: !s.chatOpen })),
  setChatInput: (input) => set({ chatInput: input }),

  resetChat: () => set({
    chatMessages: [],
    chatInput: '',
    chatOpen: false,
    chatLoading: false,
    chatHistoryLoading: false,
    copiedMessageIndex: null,
    exportingChat: false,
  }),

  loadChatHistory: (analysisId) => {
    if (!analysisId) return Promise.resolve();
    if (_chatHistoryInflight) return _chatHistoryInflight;
    _chatHistoryInflight = (async () => {
      set({ chatHistoryLoading: true });
      try {
        const response = await getChatHistory(analysisId);
        if (response.messages && response.messages.length > 0) {
          set({ chatMessages: response.messages });
          trackEvent('Chat History Loaded', { analysisId, messageCount: response.messages.length });
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      } finally {
        set({ chatHistoryLoading: false });
        _chatHistoryInflight = null;
      }
    })();
    return _chatHistoryInflight;
  },

  sendMessage: async (analysisId, suggestedQuestion = null) => {
    const { chatInput, chatLoading } = get();
    const inputValue = typeof chatInput === 'string' ? chatInput : '';
    const messageToSend = suggestedQuestion || inputValue.trim();
    if (!analysisId || !messageToSend || chatLoading) return;

    const userMessage = messageToSend;
    set({ chatInput: '', chatLoading: true });
    set((s) => ({
      chatMessages: [...s.chatMessages, { role: 'user' as const, content: userMessage, timestamp: new Date().toISOString() }]
    }));

    const chatStartTime = Date.now();

    try {
      trackEvent('Chat Message Sent', {
        analysisId,
        messageLength: userMessage.length,
        isSuggestion: !!suggestedQuestion
      });

      const response = await chatWithAnalysis(analysisId, userMessage, false);

      const responseTime = Date.now() - chatStartTime;
      trackEvent('Chat Response Received', {
        analysisId,
        responseTime,
        responseLength: response.reply?.length || 0
      });

      if (response.messages && response.messages.length > 0) {
        const messagesWithTimestamps = response.messages.map((msg: any) => ({
          ...msg,
          timestamp: msg.timestamp || new Date().toISOString()
        }));
        set({ chatMessages: messagesWithTimestamps });
      } else {
        set((s) => ({
          chatMessages: [...s.chatMessages, {
            role: 'assistant' as const,
            content: response.reply || 'No response received.',
            timestamp: new Date().toISOString()
          }]
        }));
      }
    } catch (error: any) {
      console.error('Chat error:', error);
      trackError('Chat Error', error.message);

      const errorMsg = error.response?.data?.detail || error.message || 'Sorry, I encountered an error. Please try again.';
      set((s) => ({
        chatMessages: [...s.chatMessages, {
          role: 'assistant' as const,
          content: `⚠️ **Error**: ${errorMsg}`,
          isError: true,
          timestamp: new Date().toISOString()
        }],
        chatInput: userMessage,
      }));
    } finally {
      set({ chatLoading: false });
    }
  },

  copyMessage: (message, index, analysisId) => {
    navigator.clipboard.writeText(message.content).then(() => {
      set({ copiedMessageIndex: index });
      trackEvent('Chat Message Copied', { analysisId, messageLength: message.content.length });
      setTimeout(() => set({ copiedMessageIndex: null }), 2000);
    }).catch((err) => {
      console.error('Failed to copy:', err);
    });
  },

  exportChat: async (analysisId) => {
    const { chatMessages } = get();
    if (!analysisId || chatMessages.length === 0) return;

    set({ exportingChat: true });
    try {
      trackEvent('Chat Export Started', { analysisId, messageCount: chatMessages.length });

      const chatText = chatMessages.map((msg) => {
        const role = msg.role === 'user' ? 'You' : 'QUAZI';
        const time = msg.timestamp ? ` (${formatMessageTime(msg.timestamp)})` : '';
        return `${role}${time}:\n${msg.content}\n\n`;
      }).join('---\n\n');

      const blob = new Blob([chatText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `QUAZI_Chat_${analysisId}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      trackEvent('Chat Export Completed', { analysisId });
    } catch (error: any) {
      console.error('Export error:', error);
      trackError('Chat Export Failed', error.message);
    } finally {
      set({ exportingChat: false });
    }
  },
}));
